import { Link, useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Building2,
  FileText,
  Landmark,
  ListChecks,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Store,
  Wallet,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useHealth } from "../lib/health";
import { isTexStaff, useMe, useSignOut } from "../lib/session";

const navLink =
  "mb-1 flex w-full items-center gap-3 rounded-lg px-3.5 py-2.5 font-sans text-[15px] font-medium text-neutral-500 transition-colors hover:bg-white/60 hover:text-neutral-800 [&.active]:bg-white [&.active]:text-neutral-900 [&.active]:shadow-sm [&.active]:ring-1 [&.active]:ring-black/5 [&.active>svg]:text-tex-red";

function NavIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon size={19} strokeWidth={2} className="shrink-0 text-neutral-400" />;
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="px-3 pb-1.5 pt-3 font-sans text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
        {label}
      </div>
      {children}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const health = useHealth();
  const me = useMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const demo = health.data?.app_mode === "demo";
  const user = me.data ?? null;

  const showRequests = isTexStaff(user) || (user?.dealers.length ?? 0) > 0;
  const showMyFinancing = (user?.cases.length ?? 0) > 0;
  const showOutbox = demo && user?.tex_role === "ops_admin";
  const showDesk = showRequests || showMyFinancing;
  const showTex = isTexStaff(user) || showOutbox;

  const close = () => setOpen(false);

  const sidebar = (
    <>
      <div className="flex h-[var(--tex-header-h)] shrink-0 items-center justify-between px-4">
        <Link to="/" onClick={close} className="flex items-center gap-2 transition-opacity hover:opacity-80">
          <img src="/tex-logo.png" alt="Tex" width={36} height={36} className="h-9 w-9 shrink-0 translate-y-2" />
          <span
            className="translate-y-2 whitespace-nowrap font-display text-3xl font-black uppercase tracking-tight text-tex-red"
            style={{ letterSpacing: "-0.04em" }}
          >
            Tex Finance
          </span>
        </Link>
        <button
          type="button"
          onClick={close}
          className="-mr-2 p-2 text-neutral-500 transition-colors hover:text-neutral-800 lg:hidden"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4" onClick={close}>
        {showDesk && (
          <NavGroup label="Desk">
            {showRequests && (
              <Link to="/cases" search={{}} className={navLink}>
                <NavIcon icon={FileText} />
                <span className="truncate">Requests</span>
              </Link>
            )}
            {showMyFinancing && (
              <Link to="/my-cases" className={navLink}>
                <NavIcon icon={Wallet} />
                <span className="truncate">My financing</span>
              </Link>
            )}
          </NavGroup>
        )}
        {showTex && (
          <NavGroup label="Tex">
            {isTexStaff(user) && (
              <>
                <Link to="/tex/queue" search={{}} className={navLink}>
                  <NavIcon icon={ListChecks} />
                  <span className="truncate">Queue</span>
                </Link>
                <Link to="/dealers" className={navLink}>
                  <NavIcon icon={Store} />
                  <span className="truncate">Dealers</span>
                </Link>
                <Link to="/tex/partners" className={navLink}>
                  <NavIcon icon={Landmark} />
                  <span className="truncate">Partners</span>
                </Link>
                <Link to="/tex/jurisdictions" className={navLink}>
                  <NavIcon icon={MapPin} />
                  <span className="truncate">Jurisdictions</span>
                </Link>
                <Link to="/tex/reports" className={navLink}>
                  <NavIcon icon={BarChart3} />
                  <span className="truncate">Reports</span>
                </Link>
              </>
            )}
            {showOutbox && (
              <Link to="/outbox" className={navLink}>
                <NavIcon icon={Mail} />
                <span className="truncate">Outbox</span>
              </Link>
            )}
          </NavGroup>
        )}
        {(user?.dealers.length ?? 0) > 0 && (
          <NavGroup label="Dealership">
            {user?.dealers.map((d) => (
              <Link
                key={d.dealer_id}
                to="/dealers/$dealerId"
                params={{ dealerId: d.dealer_id }}
                className={navLink}
              >
                <NavIcon icon={Building2} />
                <span className="truncate">My dealership</span>
              </Link>
            ))}
          </NavGroup>
        )}
      </nav>

      <div className="shrink-0 border-t border-stone-200 px-4 py-4">
        {user ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tex-blue font-display text-sm font-bold uppercase text-white">
                {user.user.email.slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-sans text-sm font-medium text-neutral-800">
                  {user.tex_role ? `Tex ${user.tex_role.replace("_", " ")}` : "Signed in"}
                </div>
                <div className="truncate font-mono text-xs text-neutral-400" title={user.user.email}>
                  {user.user.email}
                </div>
              </div>
            </div>
            <button
              type="button"
              title="Sign out"
              aria-label="Sign out"
              disabled={signOut.isPending}
              onClick={() =>
                signOut.mutate(undefined, {
                  onSettled: () => void navigate({ to: "/" }),
                })
              }
              className="shrink-0 cursor-pointer rounded p-1.5 text-neutral-400 transition-colors hover:bg-white/60 hover:text-neutral-700 disabled:opacity-50"
            >
              <LogOut size={15} />
            </button>
          </div>
        ) : me.data === null ? (
          <Link
            to="/sign-in"
            onClick={close}
            className="block rounded-lg bg-tex-blue px-3.5 py-2 text-center font-display text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[var(--Classic-Blue-hover)]"
          >
            Sign in
          </Link>
        ) : null}
        {health.data && (
          <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-neutral-400">
            v{health.data.version}
            {demo && <span className="ml-2 text-amber-600">demo</span>}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="flex overflow-hidden bg-stone-100/70 font-sans" style={{ height: "calc(100vh / var(--tex-zoom, 1))" }}>
      <aside className="sticky top-0 hidden w-60 flex-col lg:flex" style={{ height: "calc(100vh / var(--tex-zoom, 1))" }}>
        {sidebar}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <aside className="relative flex h-full w-64 max-w-[80vw] flex-col bg-stone-100 shadow-xl">{sidebar}</aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden bg-white lg:my-3 lg:mr-3 lg:rounded-2xl lg:shadow-sm lg:ring-1 lg:ring-black/5">
        <header className="flex items-center gap-3 border-b border-neutral-100 bg-white px-3 sm:px-6 lg:hidden" style={{ height: "var(--tex-header-h)" }}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="-ml-2 p-2 text-neutral-500 transition-colors hover:text-neutral-800"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="font-display text-xl font-black uppercase tracking-tight text-tex-red">
            Tex Finance
          </Link>
        </header>

        <main className="flex flex-1 flex-col overflow-y-auto lg:rounded-2xl">
          {demo && (
            <div
              role="status"
              className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-center font-sans text-xs text-amber-900"
            >
              Demo mode: synthetic data and fictional lenders. Nothing here is sent to a real funding partner.
            </div>
          )}
          <div className="tex-route-enter mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
